import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import * as messageService from '../../services/messageService';
import { API_BASE_URL } from '../../config/api';
import './MobileContactsPage.css';

const getAvatarUrl = (avatar) => {
  if (!avatar) return null;
  if (avatar.startsWith('http')) return avatar;
  if (avatar.startsWith('/uploads')) return avatar;
  return `${API_BASE_URL}${avatar}`;
};

const MobileContactsPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  const fetchContacts = useCallback(async () => {
    if (!user?.id) return;
    try {
      setLoading(true);
      const data = await messageService.getContacts(user.id);
      setContacts(Array.isArray(data) ? data : []);
    } catch (_) {
      setContacts([]);
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => { fetchContacts(); }, [fetchContacts]);

  const filtered = contacts.filter(c => {
    if (!searchQuery) return true;
    return (c.name || '').toLowerCase().includes(searchQuery.toLowerCase());
  });

  // Group alphabetically
  const grouped = {};
  filtered.forEach(c => {
    const letter = (c.name || '?').charAt(0).toUpperCase();
    if (!grouped[letter]) grouped[letter] = [];
    grouped[letter].push(c);
  });
  const sortedLetters = Object.keys(grouped).sort();

  return (
    <div className="mcp-page">
      {/* Header */}
      <div className="mcp-header">
        <button className="mcp-back-btn" onClick={() => navigate(-1)} aria-label="Back">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <polyline points="15 18 9 12 15 6" />
          </svg>
        </button>
        <h1 className="mcp-title">Contacts</h1>
      </div>

      {/* Search */}
      <div className="mcp-search-wrap">
        <div className="mcp-search-bar">
          <svg className="mcp-search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input
            className="mcp-search-input"
            type="text"
            placeholder="Search"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
          />
          {searchQuery && (
            <button className="mcp-clear-btn" onClick={() => setSearchQuery('')} aria-label="Clear">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          )}
        </div>
      </div>

      {/* Body */}
      <div className="mcp-body">
        {loading ? (
          <div className="mcp-loading">
            {[1, 2, 3, 4, 5].map(i => (
              <div key={i} className="mcp-skel">
                <div className="mcp-skel-avatar" />
                <div className="mcp-skel-lines">
                  <div className="mcp-skel-line mcp-skel-name" />
                  <div className="mcp-skel-line mcp-skel-sub" />
                </div>
              </div>
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="mcp-empty">
            <div className="mcp-empty-icon">
              <svg viewBox="0 0 48 48" fill="none">
                <circle cx="24" cy="24" r="22" stroke="#ff6a00" strokeWidth="2" />
                <line x1="24" y1="16" x2="24" y2="26" stroke="#ff6a00" strokeWidth="2.5" strokeLinecap="round" />
                <circle cx="24" cy="32" r="1.5" fill="#ff6a00" />
              </svg>
            </div>
            <p className="mcp-empty-text">No contacts</p>
          </div>
        ) : (
          <div className="mcp-list">
            {sortedLetters.map(letter => (
              <div key={letter} className="mcp-group">
                <div className="mcp-group-label">{letter}</div>
                {grouped[letter].map(contact => {
                  const avatarUrl = getAvatarUrl(contact.avatar);
                  const initial = (contact.name || '?').charAt(0).toUpperCase();
                  return (
                    <button
                      key={contact.id}
                      className="mcp-contact-row"
                      onClick={() => {
                        if (contact.conversationId) {
                          navigate(`/mobile/chat/${contact.conversationId}`);
                        }
                      }}
                      type="button"
                    >
                      <div className="mcp-avatar">
                        {avatarUrl
                          ? <img src={avatarUrl} alt={contact.name} />
                          : <span>{initial}</span>
                        }
                      </div>
                      <div className="mcp-contact-info">
                        <div className="mcp-contact-name">{contact.name}</div>
                        {contact.role && contact.role !== 'User' && (
                          <div className="mcp-contact-sub">{contact.role}</div>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default MobileContactsPage;
